import { RootState, runAppSaga } from 'app/state/store'
import {
  BuilderGenAIResourceState,
  KeplerLayer,
  BuilderGenAIDataset,
  LayerType,
  BuilderAddSourceGenAIAction,
  BuilderRemoveSourceGenAIAction,
  BuilderAddLayerGenAIAction,
  BuilderRemoveLayerGenAIAction,
  BuilderSetLayerStyleGenAIAction,
  MapDatasetEnriched,
  BuilderGenAIAuthor,
  BuilderGenAILayer,
  KeplerTableField,
  KeplerLayerConfig,
  PopupEvent,
  PopupStyle,
  PopupFormatSettings,
  DatasetConfigForAgent,
  PopupSettings,
  KeplerScaleType
} from '../types'
import { all, put, putResolve } from 'redux-saga/effects'
import {
  addGenAIThreadDataset,
  addGenAIThreadLayer,
  addLayerToExistingDatasetRequest,
  getEnrichedDatasetById,
  loadAttributeStatsRequest,
  removeGenAIThreadDataset,
  removeGenAIThreadLayer,
  setGenAIThread,
  setLayerPopupSettings,
  setPopupSettings,
  updateGenAIThreadDataset,
  updateGenAIThreadLayer
} from '../state/builderSlice'
import { appStoreDispatch, appStoreSelect, waitForSelectorValue } from 'app/state/storeUtils'
import {
  layerConfigChange,
  layerVisualChannelConfigChange,
  removeDataset,
  removeLayer,
  reorderLayer
} from '@carto/kepler.gl/actions'
import { Formats, MapType } from 'features/maps/types'
import { getLayerLoader } from '../state/builderSagas'
import { hexToRGB, isValidHex } from 'features/common/utils/colors'
import { KeplerColorRange } from '../types'
import { clamp } from 'features/common/utils/clamp'
import { RequestStatus } from 'features/common'
import { getDefaultQualitativeColors, getDefaultSequentialColors } from '../ui/theme/cartoColors'
import { isInRange } from 'features/common/utils/isInRange'
import { FIELD_TYPES_NUMERIC } from '../ui/builderConstants'
import { MAX_HOVER_FIELDS } from '../ui/builderConstants'
import { getDatasetConfigForAgent } from '../state/builderSliceUtils'

export function getAgentLayerIds(): string[] {
  const agentLayers: BuilderGenAILayer[] = appStoreSelect((state: RootState) => state.builder.genAiThread?.layers || [])
  return agentLayers.map((d) => d.layerId).filter(Boolean) as string[]
}

export function getAgentDatasetIds(): string[] {
  const agentDatasets: BuilderGenAIDataset[] = appStoreSelect(
    (state: RootState) => state.builder.genAiThread?.datasets || []
  )
  return agentDatasets.map((d) => d.datasetId).filter(Boolean) as string[]
}

export function isAgentLayer(layerId: string): boolean {
  return getAgentLayerIds().includes(layerId)
}

export function isAgentDataset(datasetId: string): boolean {
  return getAgentDatasetIds().includes(datasetId)
}

export async function deleteAgentConversation(): Promise<void> {
  await removeAllAgentLayers()
  await removeAllAgentDatasets()
  appStoreDispatch(setGenAIThread({}))
}

export async function interruptAgentConversation(): Promise<void> {
  const prevConversation = appStoreSelect((state: RootState) => state.builder.genAiConversation)
  const prevThread = appStoreSelect((state: RootState) => state.builder.genAiThread)

  const lastResponseIndex = prevConversation.findLastIndex((item) => {
    switch (item.author) {
      case BuilderGenAIAuthor.System:
        return item
      case BuilderGenAIAuthor.Bot:
        // API bundles 'response' and 'action_frontend' into one event, with
        // optional message. Skip if no message.
        return item.type === 'response' && item.message
      default:
        return false
    }
  })
  const lastResponse = lastResponseIndex >= 0 ? prevConversation[lastResponseIndex] : undefined
  const lastParent = (lastResponse as { parent?: string })?.parent

  const conversation = prevConversation.slice(0, lastResponseIndex + 1)
  const thread = prevThread && lastParent ? { ...prevThread, parent: lastParent } : undefined

  // TODO(sc-507885): Remove any datasets or layers created by the conversation items being removed.
  appStoreDispatch(setGenAIThread({ thread, conversation }))
}

export type AgentActionRequest = { actionId: string }

export type AgentAddDatasetRequest = AgentActionRequest & BuilderAddSourceGenAIAction['params']
export type AgentAddDatasetResponse = DatasetConfigForAgent

export async function addAgentDataset(request: AgentAddDatasetRequest): Promise<AgentAddDatasetResponse> {
  const { actionId, connection: connectionName, source, type, geoColumn } = request

  try {
    const threadDataset: BuilderGenAIDataset = { actionId, state: BuilderGenAIResourceState.Loading }

    appStoreDispatch(addGenAIThreadDataset(threadDataset))

    const mapId = appStoreSelect((state: RootState) => state.maps.currentMap?.id as string)
    const connection = appStoreSelect((state: RootState) =>
      state.connections.myConnections.find((conn) => conn.name === connectionName)
    )

    assertAsAgent(connection, `Connection not found for name, "${connectionName}"`)
    assertAsAgent(type in MapType, `Unsupported source type, "${type}"`)

    const datasetId = await runAppSaga(function* () {
      let columns: string[] | undefined

      const loader = getLayerLoader({
        mapId,
        connection: connection.name,
        provider: connection.provider_id,
        source,
        type,
        columns,
        geoColumn,
        format: Formats.TILEJSON,
        createLayer: false
      })

      const datasetId = loader.datasetId

      // Link agent and dataset before calling .add(). In addition to hiding the dataset in the UI,
      // this prevents the dataset from being saved — which would overwrite 'datasetId'.
      yield put(
        updateGenAIThreadDataset({
          actionId,
          dataset: { state: BuilderGenAIResourceState.Loading, datasetId }
        })
      )

      yield loader.add()
      yield loader.getKeplerDataset()

      return datasetId
    })

    await waitForSelectorValue(
      (state) => state.maps.currentMapDatasets.some((d) => d.id === datasetId && d.status !== RequestStatus.Pending),
      true
    )

    // Datasets may be added, but in an error state, for example if the SQL query contains an error.
    const dataset = appStoreSelect((state) => state.maps.currentMapDatasets.find((d) => d.id === datasetId))
    if (dataset?.status !== RequestStatus.Fulfilled) {
      appStoreDispatch(removeDataset(datasetId))
      throw new AgentError(dataset?.errorMessage || 'Source creation failed')
    }

    appStoreDispatch(
      updateGenAIThreadDataset({
        actionId,
        dataset: { state: BuilderGenAIResourceState.Success, datasetId }
      })
    )

    const datasetEnriched: MapDatasetEnriched = appStoreSelect((state: RootState) =>
      getEnrichedDatasetById(state, datasetId)
    )

    return getDatasetConfigForAgent(datasetEnriched)
  } catch (e) {
    console.error(e)

    appStoreDispatch(
      updateGenAIThreadDataset({
        actionId,
        dataset: { state: BuilderGenAIResourceState.Error, error: 'Source creation failed' }
      })
    )

    throw e
  }
}

export type AgentRemoveDatasetRequest = Partial<AgentActionRequest> & BuilderRemoveSourceGenAIAction['params']
export type AgentRemoveDatasetResponse = void

export async function removeAgentDataset({ datasetId }: AgentRemoveDatasetRequest): Promise<void> {
  try {
    const datasets = appStoreSelect((state) => state.maps.currentMapDatasets)
    const threadDatasets = appStoreSelect((state) => state.builder.genAiThread?.datasets || [])
    const isThreadDataset = threadDatasets.some((td) => td.datasetId === datasetId)

    assertAsAgent(
      datasets.some((d) => d.id === datasetId),
      `No source found with ID, "${datasetId}"`
    )
    assertAsAgent(isThreadDataset, `Cannot remove non-AI dataset, "${datasetId}"`)

    await runAppSaga(function* () {
      yield put(removeDataset(datasetId))
    })

    // Ensure dataset is associated with AI Agent until it is fully removed.
    await runAppSaga(function* () {
      yield put(removeGenAIThreadDataset({ datasetId }))
    })
  } catch (e) {
    console.error(e)

    throw e
  }
}

/** Internal-only; needs actionId if exposed to agent. */
async function removeAllAgentDatasets(): Promise<void> {
  const threadDatasets = appStoreSelect((state) => state.builder.genAiThread?.datasets || [])

  try {
    await runAppSaga(function* () {
      yield all(
        threadDatasets.map(function* ({ datasetId }) {
          if (datasetId) {
            yield put(removeDataset(datasetId))
          }
        })
      )
    })

    // Ensure dataset is associated with AI Agent until it is fully removed.
    await runAppSaga(function* () {
      yield all(
        threadDatasets.map(function* ({ datasetId }) {
          if (datasetId) {
            yield put(removeGenAIThreadDataset({ datasetId }))
          }
        })
      )
    })
  } catch (e) {
    console.error(e)

    throw e
  }
}

export type AgentAddLayerRequest = AgentActionRequest & BuilderAddLayerGenAIAction['params']
export type AgentAddLayerResponse = { layerId: string; type: LayerType }

export async function addAgentLayer(request: AgentAddLayerRequest): Promise<AgentAddLayerResponse> {
  const { actionId, datasetId, label, hoverColumns } = request

  try {
    appStoreDispatch(addGenAIThreadLayer({ actionId, state: BuilderGenAIResourceState.Loading }))

    // Execution must block until layer is added.
    await runAppSaga(function* () {
      yield putResolve(addLayerToExistingDatasetRequest(datasetId))
    })

    // By default, new layers are appended last.
    const layers: KeplerLayer[] = appStoreSelect((state: RootState) => state.keplerGl.map.visState.layers)
    const layerOrder: number[] = appStoreSelect((state: RootState) => state.keplerGl.map.visState.layerOrder)
    const layer = layers[layers.length - 1]

    assertAsAgent(layer, 'Something went wrong while creating the layer')

    const layerId = layer.id

    // Mark as AI-created layer, _then_ update label. LayerPanelHeader keeps
    // internal state, and tries to change label back, but component is not mounted
    // for layers once marked as AI-generated. [sc-506406].
    await runAppSaga(function* () {
      yield put(updateGenAIThreadLayer({ actionId, layer: { state: BuilderGenAIResourceState.Success, layerId } }))
    })

    await runAppSaga(function* () {
      // Reorder so new layer appears first.
      yield put(reorderLayer([layers.length - 1, ...layerOrder.slice(0, -1)]))
      yield put(layerConfigChange(layer, { label }))
    })

    // Configure hover popup if hoverColumns provided.
    if (hoverColumns && hoverColumns?.length > 0) {
      await setAgentLayerPopupSettings({
        layerId,
        datasetId,
        hoverColumns
      })
    }

    return { layerId, type: layer.type }
  } catch (e) {
    console.error(e)

    appStoreDispatch(
      updateGenAIThreadLayer({
        actionId,
        layer: { state: BuilderGenAIResourceState.Error, error: 'Layer creation failed' }
      })
    )

    throw e
  }
}

async function setAgentLayerPopupSettings({
  layerId,
  datasetId,
  hoverColumns
}: {
  layerId: string
  datasetId: string
  hoverColumns: string[]
}): Promise<void> {
  const dataset: MapDatasetEnriched = appStoreSelect((state: RootState) => getEnrichedDatasetById(state, datasetId))
  assertAsAgent(dataset, `No source found with ID, "${datasetId}"`)

  const invalidColumns = hoverColumns.filter((id) => !dataset.fields.some((f) => f.id === id))
  assertAsAgent(invalidColumns.length === 0, `Unknown columns: ${invalidColumns.join(', ')}`)

  const hoverConfig: PopupFormatSettings = {
    // [sc-506551] Given more than MAX_HOVER_FIELDS columns, ignore the rest without error.
    fields: hoverColumns.slice(0, MAX_HOVER_FIELDS).map((name) => ({ name })),
    style: PopupStyle.Light,
    templateMode: false
  }

  try {
    await runAppSaga(function* () {
      yield put(
        setLayerPopupSettings({
          layerId,
          settings: {
            enabled: true,
            [PopupEvent.Hover]: hoverConfig
          }
        })
      )
    })
  } catch (e) {
    console.error('Failed to configure hover popup:', e)
    throw new AgentError('Failed to configure hover popup')
  }
}

export type AgentRemoveLayerRequest = Partial<AgentActionRequest> & BuilderRemoveLayerGenAIAction['params']
export type AgentRemoveLayerResponse = void

export async function removeAgentLayer({ layerId }: AgentRemoveLayerRequest): Promise<void> {
  try {
    const threadLayers = appStoreSelect((state: RootState) => state.builder.genAiThread?.layers || [])
    const layers: KeplerLayer[] = appStoreSelect((state: RootState) => state.keplerGl.map.visState.layers)
    const layerIdx = layers.findIndex((layer) => layer.id === layerId)
    const isThreadLayer = threadLayers.some((tl) => tl.layerId === layerId)

    assertAsAgent(layerIdx !== -1, `No layer found with ID, "${layerId}"`)
    assertAsAgent(isThreadLayer, `Cannot remove non-AI layer, "${layerId}"`)

    // Remove any popup settings for the removed layer.
    const popupSettings = appStoreSelect((state: RootState) => state.builder.popupSettings)
    let updatedPopupSettings: PopupSettings | undefined
    if (popupSettings?.layers?.[layerId]) {
      updatedPopupSettings = { ...popupSettings, layers: { ...popupSettings.layers } }
      delete updatedPopupSettings.layers[layerId]
    }

    await runAppSaga(function* () {
      yield put(removeLayer(layerIdx))
      yield put(removeGenAIThreadLayer({ layerId }))

      if (updatedPopupSettings) {
        yield put(setPopupSettings(updatedPopupSettings))
      }
    })
  } catch (e) {
    console.error(e)
    throw e
  }
}

/** Internal-only; needs actionId if exposed to agent. */
async function removeAllAgentLayers(): Promise<void> {
  try {
    const agentLayers = appStoreSelect((state) => state.builder.genAiThread?.layers || [])

    // Remove layers sequentially, layerIdx may change mid-operation.
    // TODO: After Kepler removal, consider refactoring removeLayer to use ID, not index.
    for (const { layerId } of agentLayers) {
      const layers: KeplerLayer[] = appStoreSelect((state: RootState) => state.keplerGl.map.visState.layers)
      const layerIdx = layers.findIndex((layer) => layer.id === layerId)
      if (!layerId || layerIdx === -1) continue

      await removeAgentLayer({ layerId })
    }
  } catch (e) {
    console.error('_removeAllAgentLayers', e)
    throw e
  }
}

export type AgentSetLayerStyleRequest = AgentActionRequest & BuilderSetLayerStyleGenAIAction['params']
export type AgentSetLayerStyleResponse = void

const DEFAULT_AGENT_LAYER_CONFIG = {
  color: [255, 255, 255, 255],
  colorField: undefined,
  colorScale: undefined,
  radiusField: undefined,
  visConfig: {
    opacity: 0.8,
    filled: true,
    colorRange: undefined,
    stroked: false,
    strokeOpacity: 0.8,
    strokeColor: [255, 255, 255, 255],
    thickness: 1,
    radius: undefined,
    radiusRange: undefined
  }
}

export async function setLayerStyle(request: AgentSetLayerStyleRequest): Promise<void> {
  const { layerId, ...style } = request

  try {
    const threadLayers = appStoreSelect((state: RootState) => state.builder.genAiThread?.layers || [])
    const layers: KeplerLayer[] = appStoreSelect((state: RootState) => state.keplerGl.map.visState.layers)
    const layer = layers.find((layer) => layer.id === layerId)
    const isThreadLayer = threadLayers.some((tl) => tl.layerId === layerId)

    assertAsAgent(layer, `No layer found with ID, "${layerId}"`)
    assertAsAgent(isThreadLayer, `Cannot change non-AI layer, "${layerId}"`)

    const dataset: MapDatasetEnriched = appStoreSelect((state: RootState) =>
      getEnrichedDatasetById(state, layer.config.dataId)
    )

    const config: KeplerLayerConfig = {
      ...layer.config,
      visConfig: { ...layer.config.visConfig }
    }

    const attributes = new Set<string>()
    const visualChannels = new Set<string>()

    // Despite tool instructions, agent often returns an empty
    // string for parameters instead of 'undefined'. Handle both.

    if (style.opacity !== undefined) {
      config.visConfig.opacity = clamp(style.opacity, 0.0, 1.0)
      config.visConfig.strokeOpacity = clamp(style.opacity, 0.0, 1.0)
    }

    if (style.fillColor) {
      config.color = hexToRGB(style.fillColor)
      config.colorField = DEFAULT_AGENT_LAYER_CONFIG.colorField
      config.colorScale = DEFAULT_AGENT_LAYER_CONFIG.colorScale
      config.visConfig.colorRange = DEFAULT_AGENT_LAYER_CONFIG.visConfig.colorRange
    } else if (style.fillColorColumn) {
      const colorField = dataset.fields.find((f) => f.id === style.fillColorColumn)
      assertAsAgent(colorField, `No field with name, "${style.fillColorColumn}"`)

      config.color = DEFAULT_AGENT_LAYER_CONFIG.color
      config.colorField = colorField
      config.colorScale = getDefaultScaleForField(colorField)
      config.visConfig.colorRange = createLayerColorRange(colorField, style.fillColorPalette)

      attributes.add(colorField.id)
      visualChannels.add('color')
    }

    if (style.outlineWidth !== undefined) {
      config.visConfig.stroked = style.outlineWidth > 0
      config.visConfig.thickness = clamp(style.outlineWidth, 0, 50) // Must match Latitude.
    }

    if (style.outlineColor) {
      config.visConfig.strokeColor = hexToRGB(style.outlineColor)
    }

    if (style.radius !== undefined) {
      // Agent frequently returns radius=0 for non-point layers.
      assertAsAgent(layer.meta.featureTypes?.point || style.radius === 0, 'Only point layers support "radius"')
      config.radiusField = DEFAULT_AGENT_LAYER_CONFIG.radiusField
      config.visConfig.radius = clamp(style.radius, 0, 50) // Must match Latitude.
      config.visConfig.radiusRange = DEFAULT_AGENT_LAYER_CONFIG.visConfig.radiusRange
    } else if (style.radiusColumn) {
      const radiusField = dataset.fields.find((f) => f.id === style.radiusColumn)
      assertAsAgent(layer.meta.featureTypes?.point, 'Only point layers support "radiusColumn"')
      assertAsAgent(radiusField, `No field with name, "${style.radiusColumn}"`)
      assertAsAgent(FIELD_TYPES_NUMERIC.includes(radiusField.type), '"radiusColumn" must be numeric')

      config.radiusField = radiusField
      config.visConfig.radius = DEFAULT_AGENT_LAYER_CONFIG.visConfig.radius
      config.visConfig.radiusRange = [0, 50]

      attributes.add(radiusField.id)
      visualChannels.add('radius')
    }

    await runAppSaga(function* () {
      yield put(layerConfigChange(layer, config))
      yield all(
        Array.from(attributes).map((attribute) => put(loadAttributeStatsRequest({ datasetId: dataset.id, attribute })))
      )
      yield all(
        Array.from(visualChannels).map((channel) => put(layerVisualChannelConfigChange(layer, config, channel)))
      )
    })
  } catch (e) {
    console.error(e)

    throw e
  }
}

function createLayerColorRange(field: KeplerTableField, colors?: string[]): KeplerColorRange {
  if (colors?.length) {
    assertAsAgent(isInRange(colors.length, 2, 20), `Color palette must contain 2-20 colors, got ${colors.length}`)
    for (const color of colors) {
      assertAsAgent(isValidHex(color), `Invalid hex color format: "${color}". Expected format: #RRGGBB`)
    }
    return { name: 'Custom Palette', category: 'Custom', type: 'custom', colors }
  }

  return FIELD_TYPES_NUMERIC.includes(field.type) ? getDefaultSequentialColors() : getDefaultQualitativeColors()
}

function getDefaultScaleForField(field: KeplerTableField): KeplerScaleType {
  if (FIELD_TYPES_NUMERIC.includes(field.type)) {
    return KeplerScaleType.Quantile
  }
  return KeplerScaleType.Ordinal
}

/** Asserts the condition or throws an {@link AgentError}. */
function assertAsAgent(condition: unknown, msg: string): asserts condition {
  if (!condition) {
    throw new AgentError(msg)
  }
}

/**
 * Subclass for errors the Agent should receive verbatim. Errors not explicitly
 * thrown or wrapped as {@link AgentError} will be shown to the Agent only as a
 * generic "something went wrong"-style message.
 */
export class AgentError extends Error {}
